import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, BookmarkIcon, CheckCircle2Icon, EyeOffIcon, HeartIcon, InfoIcon, LockIcon, MessageSquareIcon, ShieldAlertIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Input } from '../ui/Field';
import { ReportControls } from '../trust/ReportControls';
import { useSocial } from '../../contexts/SocialContext';
import { POST_KIND_LABEL, isOfficial, pollClosed, pollTotal, relativeTime, visibleComments } from '../../utils/social';
import type { SocialPost } from '../../types/social';

/**
 * One post. Official institutional content is deliberately given a different
 * frame from student content so the two can never be confused, and a removed
 * post keeps its place with the reason stated rather than disappearing.
 */
export function PostCard({
  post
}: {
  post: SocialPost;
}) {
  const {
    toggleReaction,
    toggleSave,
    comment,
    answer,
    resolve,
    vote,
    lockComments,
    hide
  } = useSocial();
  const [draft, setDraft] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const official = isOfficial(post.author.kind);
  const comments = visibleComments(post);
  if (post.state === 'removed') {
    return <article className="rounded-card border border-line bg-canvas p-5" data-id="element-5036" data-mp="2dtc2khp7kcwj">
        <div className="flex flex-wrap items-center gap-2" data-id="element-5037" data-mp="1zuchtl4tgi5n">
          <Badge tone="neutral" icon={<ShieldAlertIcon className="h-4 w-4" data-id="element-5039" data-mp="23yahpy1ytwzr" />} data-id="element-5038" data-mp="23xr7z4mgyewt">
            Removed by moderation
          </Badge>
          <span className="text-body-sm text-ink-subtle" data-id="element-5040" data-mp="1pzar4kuw780y">{relativeTime(post.at)}</span>
        </div>
        <p className="mt-2 max-w-[74ch] text-body text-ink" data-id="element-5041" data-mp="1uxcidt2h2dni">{post.removalReason}</p>
      </article>;
  }
  return <article className={official ? 'rounded-card border-2 border-brand-200 bg-brand-50 p-5 sm:p-6' : 'rounded-card border border-line bg-surface p-5 sm:p-6'} data-id="element-5042" data-mp="3k9o3gqjf08vt">
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-5043" data-mp="3nrbuwk0j533t">
        <div className="flex min-w-0 items-start gap-3" data-id="element-5044" data-mp="3iwofdani7j1d">
          <span aria-hidden className={`grid h-11 w-11 shrink-0 place-items-center rounded-full text-body font-semibold ${official ? 'bg-brand-700 text-white' : 'bg-canvas text-ink'}`} data-id="element-5045" data-mp="3dwjwjdz4ic7z">
            {post.author.initials}
          </span>
          <div className="min-w-0" data-id="element-5046" data-mp="5gqeuvt22upc">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-5047" data-mp="5hs3y46wphq2">
              <Link to={post.author.kind === 'student' ? `/community/people/${post.author.handle}` : '/social'} className="text-body font-semibold text-ink hover:underline" data-id="element-5048" data-mp="nnthayfmrk8">
                {post.author.name}
              </Link>
              {post.author.verified && <BadgeCheckIcon aria-label="Verified account" className="h-5 w-5 shrink-0 text-brand-700" data-id="element-5049" data-mp="woij9n991110" />}
            </div>
            <p className="text-body-sm text-ink-muted" data-id="element-5050" data-mp="15mu57bz67an4">{post.author.context}</p>
            <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-5051" data-mp="1anhyk4avg3ki">
              {relativeTime(post.at)} · {post.audienceLabel}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-5052" data-mp="1fk8l9ah6lxew">
          {official && <Badge tone="brand" data-id="element-5053" data-mp="1jlk48x5jvke4">Official</Badge>}
          <Badge plain data-id="element-5054" data-mp="1odv7o9zi1obf">{POST_KIND_LABEL[post.kind]}</Badge>
        </div>
      </header>

      <p className="mt-4 max-w-[74ch] whitespace-pre-wrap text-body-lg text-ink" data-id="element-5055" data-mp="1td6o1hvyersh">{post.body}</p>

      {post.poll && <PollBlock post={post} onVote={option => vote(post.id, option)} data-id="element-5056" data-mp="1yd2qhcp06b5x" />}

      {post.kind === 'question' && <AnswerBlock post={post} onAnswer={body => answer(post.id, body)} onResolve={() => resolve(post.id)} data-id="element-5057" data-mp="23cp4hocnnvfs" />}

      {post.attached && <Link to={post.attached.to} className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-md border border-line bg-canvas px-4 py-3 hover:border-brand-300" data-id="element-5058" data-mp="300oc5vsd3cw5">
          <span className="min-w-0" data-id="element-5059" data-mp="34tzkk1cto1q6">
            <span className="block text-body font-semibold text-ink" data-id="element-5060" data-mp="34v2ao72bm051">{post.attached.label}</span>
            <span className="block text-body-sm text-ink-muted" data-id="element-5061" data-mp="2h4y6nyl7zydi">{post.attached.detail}</span>
          </span>
          <ArrowRightIcon aria-hidden className="h-5 w-5 shrink-0 text-brand-700" data-id="element-5062" data-mp="2c6mwtnuxdnva" />
        </Link>}

      <footer className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-5063" data-mp="2q6eh26c1c0hq">
        <Button variant={post.myReaction ? 'primary' : 'ghost'} size="sm" icon={<HeartIcon className="h-4 w-4" data-id="element-5065" data-mp="5d18at1z5q9s" />} onClick={() => toggleReaction(post.id)} aria-pressed={post.myReaction} data-id="element-5064" data-mp="2m1olyjp7apxv">
          {post.reactions}
        </Button>
        <Button variant="ghost" size="sm" icon={<MessageSquareIcon className="h-4 w-4" data-id="element-5067" data-mp="dw5z2dlxdyw9" />} onClick={() => setShowComments(current => !current)} aria-expanded={showComments} data-id="element-5066" data-mp="8vqnnrl7f1gx">
          {comments.length}
        </Button>
        <Button variant={post.saved ? 'secondary' : 'ghost'} size="sm" icon={<BookmarkIcon className="h-4 w-4" data-id="element-5069" data-mp="rmriq4j7ecv7" />} onClick={() => toggleSave(post.id)} aria-pressed={post.saved} data-id="element-5068" data-mp="3wnhg3thmp7d">
          {post.saved ? 'Saved' : 'Save'}
        </Button>
        <Button variant="ghost" size="sm" icon={<InfoIcon className="h-4 w-4" data-id="element-5071" data-mp="mwb6gnr4cxaf" />} onClick={() => setShowReason(current => !current)} aria-expanded={showReason} data-id="element-5070" data-mp="hxzwmd0tqms7">
          Why this?
        </Button>
        <Button variant="ghost" size="sm" icon={<EyeOffIcon className="h-4 w-4" data-id="element-5073" data-mp="1fe6q4bpu7kdb" />} onClick={() => hide(post.id)} data-id="element-5072" data-mp="1ak2hzp4u56an">
          Not interested
        </Button>
        <div className="ml-auto" data-id="element-5074" data-mp="1p688izrf05f5">
          <ReportControls subject={`${post.author.name}'s post`} surface="profile" allowBlock={post.author.kind === 'student'} data-id="element-5075" data-mp="1k99f6jfks2i0" />
        </div>
      </footer>

      {showReason && <p className="mt-3 rounded-md border border-line bg-canvas px-3 py-2.5 text-body-sm text-ink" data-id="element-5076" data-mp="1b8b97vlktkws">
          {post.reason} Reaction counts never move a post up the feed.
        </p>}

      {showComments && <div className="mt-4 border-t border-line pt-4" data-id="element-5077" data-mp="11gx5fbmldyxu">
          {post.commentsLocked ? <p className="flex items-start gap-2 text-body-sm text-ink-muted" data-id="element-5078" data-mp="whu9qhsi552j">
              <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-5079" data-mp="sjwkjeyqeiz5" />
              {official ? 'Comments are closed on official notices. Questions go to your department or to Support.' : 'The author has locked replies on this post.'}
            </p> : <>
              <ul className="space-y-3" data-id="element-5080" data-mp="2u9lmqr59soyn">
                {comments.map(entry => <li key={entry.id} className={entry.parentId ? 'ml-6 border-l-2 border-line pl-4' : ''} data-id="element-5081" data-mp="2z510lj3a2kv2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-5082" data-mp="387k0bubrohd3">
                      <span className="text-body font-medium text-ink" data-id="element-5083" data-mp="2bhjea32s8ezh">{entry.author.name}</span>
                      <span className="text-caption text-ink-subtle" data-id="element-5084" data-mp="2gful04xs69dh">
                        {relativeTime(entry.at)}
                      </span>
                    </div>
                    <p className="mt-0.5 max-w-[72ch] text-body text-ink" data-id="element-5085" data-mp="2kfv1dv4fwun6">{entry.body}</p>
                    {entry.mentions.length > 0 && <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-5086" data-mp="1srbcboatjukv">
                        Notified: {entry.mentions.map(handle => `@${handle}`).join(', ')} — a
                        mention never grants access to anything.
                      </p>}
                  </li>)}
              </ul>
              <form className="mt-4" onSubmit={event => {
          event.preventDefault();
          if (!draft.trim()) return;
          comment(post.id, draft.trim(), null);
          setDraft('');
        }} data-id="element-5087" data-mp="l7j2wnpfzatw">
                <Field label="Add a reply" htmlFor={`reply-${post.id}`} data-id="element-5088" data-mp="191u2e2l8scsj">
                  <Input id={`reply-${post.id}`} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Use @handle to mention someone" data-id="element-5089" data-mp="143sodw2ob3nx" />
                </Field>
                <div className="mt-3 flex flex-wrap gap-2" data-id="element-5090" data-mp="1rtd9olmaud0d">
                  <Button type="submit" variant="primary" size="sm" disabled={!draft.trim()} data-id="element-5091" data-mp="1rrsfix3wcw31">
                    Reply
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => lockComments(post.id, true)} data-id="element-5092" data-mp="26hws5w89i887">
                    Lock replies
                  </Button>
                </div>
              </form>
            </>}
        </div>}
    </article>;
}
function PollBlock({
  post,
  onVote
}: {
  post: SocialPost;
  onVote: (optionId: string) => void;
}) {
  if (!post.poll) return null;
  const total = pollTotal(post);
  const closed = pollClosed(post);
  const voted = post.poll.myVote !== null;
  return <div className="mt-4 rounded-md border border-line bg-canvas p-4" data-id="element-5093" data-mp="1eriikc7a7i2c">
      <ul className="space-y-2.5" data-id="element-5094" data-mp="1isarf4c8rky3">
        {post.poll.options.map(option => {
        const share = total === 0 ? 0 : Math.round(option.votes / total * 100);
        const mine = post.poll?.myVote === option.id;
        return <li key={option.id} data-id="element-5095" data-mp="19snnaas4o11c">
              {voted || closed ? <div data-id="element-5096" data-mp="1ri0kn9hs5lo0">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1" data-id="element-5097" data-mp="1rkwm37su6sh7">
                    <span className="text-body text-ink" data-id="element-5098" data-mp="1viaskrp4um5i">
                      {option.label}
                      {mine && <span className="ml-2 text-body-sm text-brand-700" data-id="element-5099" data-mp="1hmqoh2e2c8i5">Your vote</span>}
                    </span>
                    <span className="cz-num text-body-sm text-ink-muted" data-id="element-5100" data-mp="2a9barofolueu">
                      {share}% · {option.votes}
                    </span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-line" data-id="element-5101" data-mp="2f7l6c05bix0z">
                    <div className={mine ? 'h-full bg-brand-700' : 'h-full bg-ink-subtle'} style={{
                width: `${share}%`
              }} data-id="element-5102" data-mp="20iyuw9itapf1" />
                  </div>
                </div> : <Button variant="secondary" size="sm" block onClick={() => onVote(option.id)} data-id="element-5103" data-mp="2xym6gri6cym4">
                  {option.label}
                </Button>}
            </li>;
      })}
      </ul>
      <p className="mt-3 text-body-sm text-ink-muted" data-id="element-5104" data-mp="hlby1cjgsdah">
        {closed ? 'Voting has closed.' : voted ? 'Your vote is recorded. One vote per account — you cannot vote twice.' : post.poll.eligibility}
        {post.poll.anonymous && ' Individual votes are not shown to the poll creator.'}
      </p>
    </div>;
}
function AnswerBlock({
  post,
  onAnswer,
  onResolve
}: {
  post: SocialPost;
  onAnswer: (body: string) => void;
  onResolve: () => void;
}) {
  const [draft, setDraft] = useState('');
  return <div className="mt-4 space-y-3" data-id="element-5105" data-mp="cjmok1bw47oi">
      {post.answers.length === 0 ? <p className="rounded-md border border-line bg-canvas px-3 py-2.5 text-body-sm text-ink-muted" data-id="element-5106" data-mp="7l311whnenu1">
          No answers yet. Nothing is filled in on the author’s behalf.
        </p> : <ul className="space-y-3" data-id="element-5107" data-mp="1j19s26nt35db">
          {post.answers.map(entry => <li key={entry.id} className={entry.verified ? 'rounded-md border-2 border-ok-200 bg-ok-50 p-4' : 'rounded-md border border-line bg-canvas p-4'} data-id="element-5108" data-mp="gnbxw3u4acnd">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2" data-id="element-5109" data-mp="unwh313y2ahe">
                <span className="text-body font-semibold text-ink" data-id="element-5110" data-mp="3u39yeiokf0ze">{entry.author.name}</span>
                <span className="flex flex-wrap items-center gap-2" data-id="element-5111" data-mp="1zh38hwjty7n">
                  {entry.verified ? <Badge tone="ok" icon={<CheckCircle2Icon className="h-4 w-4" data-id="element-5113" data-mp="6ybum88l0v3d" />} data-id="element-5112" data-mp="6yu0zxoykpp0">
                      Verified answer
                    </Badge> : <Badge plain data-id="element-5114" data-mp="brdbglscn4pl">Personal opinion</Badge>}
                  {entry.aiGenerated && <Badge tone="warn" data-id="element-5115" data-mp="3ad7w2jd79sn5">AI generated</Badge>}
                </span>
              </div>
              <p className="mt-1.5 max-w-[72ch] text-body text-ink" data-id="element-5116" data-mp="3fcapf6jtiafa">{entry.body}</p>
              <p className="mt-1 text-caption text-ink-subtle" data-id="element-5117" data-mp="37u34cjhc8upw">
                {entry.helpful} found this helpful · {relativeTime(entry.at)}
              </p>
            </li>)}
        </ul>}

      {post.resolved ? <Badge tone="ok" icon={<CheckCircle2Icon className="h-4 w-4" data-id="element-5119" data-mp="2tvv0drlvwt58" />} data-id="element-5118" data-mp="2tv3dd56cs3qk">
          Marked resolved by the person who asked
        </Badge> : <form onSubmit={event => {
      event.preventDefault();
      if (!draft.trim()) return;
      onAnswer(draft.trim());
      setDraft('');
    }} data-id="element-5120" data-mp="2oxl4fesc5oik">
          <Field label="Answer this" htmlFor={`answer-${post.id}`} hint="Student answers are shown as personal opinion. Only an authorised voice can post a verified answer." data-id="element-5121" data-mp="2f6qfg2npgatf">
            <Input id={`answer-${post.id}`} value={draft} onChange={event => setDraft(event.target.value)} data-id="element-5122" data-mp="267bshd6qyido" />
          </Field>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-5123" data-mp="542v31gcupro">
            <Button type="submit" variant="primary" size="sm" disabled={!draft.trim()} data-id="element-5124" data-mp="95efmskdgkt0">
              Post answer
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onResolve} data-id="element-5125" data-mp="3richj50q5npt">
              Mark resolved
            </Button>
          </div>
        </form>}
    </div>;
}